import { Head, usePage } from '@inertiajs/react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import type { SharedProps } from '@/types'

/** 居中的认证卡片：登录 / 注册 / 找回密码 共用外壳，自带品牌头与 flash 提示。 */
export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}) {
  const { flash } = usePage<SharedProps>().props

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <Head title={title} />
      <div className="w-full max-w-[400px] grid gap-4">
        <div className="text-center">
          <div className="font-extrabold text-2xl bg-gradient-to-r from-[#5b7cfa] to-[#8a5bfa] bg-clip-text text-transparent">
            🎯 HabitPoint
          </div>
          <div className="text-[#707d90] text-sm mt-1">设目标 · 拆步骤 · 完成得分 · 积分兑换</div>
        </div>

        <section className="bg-white border border-[#e7ebf2] rounded-2xl shadow-[0_1px_2px_rgba(20,30,50,.05),0_8px_28px_rgba(20,30,50,.06)] px-6 py-6">
          <h1 className="text-[17px] font-bold m-0">{title}</h1>
          {subtitle && <p className="text-[#707d90] text-sm mt-1 mb-0">{subtitle}</p>}

          {flash?.notice && (
            <div className="mt-4 text-sm rounded-[10px] px-3 py-2 bg-[#e9f9ef] text-[#1d7a44] border border-[#bdeccd]">
              {flash.notice}
            </div>
          )}
          {flash?.alert && (
            <div className="mt-4 text-sm rounded-[10px] px-3 py-2 bg-[#fdecec] text-[#c0392b] border border-[#f5c6c6]">
              {flash.alert}
            </div>
          )}

          <div className="mt-5 grid gap-4">{children}</div>
        </section>

        {footer && <div className="text-center text-sm text-[#707d90]">{footer}</div>}
      </div>
    </div>
  )
}

/** 带标签与错误提示的输入框。 */
export function Field({
  label,
  error,
  ...props
}: { label: string; error?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="grid gap-1.5">
      <span className="text-sm font-semibold text-[#1b2330]">{label}</span>
      <input
        {...props}
        className={
          'border rounded-[10px] px-3 py-2.5 outline-none transition-colors ' +
          (error ? 'border-[#e0a3a3] focus:border-[#c0392b]' : 'border-[#e7ebf2] focus:border-[#5b7cfa]')
        }
      />
      {error && <span className="text-[#c0392b] text-[13px]">{error}</span>}
    </label>
  )
}

/** 主按钮，提交中禁用。 */
export function SubmitButton({ children, disabled }: { children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="bg-gradient-to-r from-[#5b7cfa] to-[#8a5bfa] text-white font-bold rounded-[10px] px-4 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-95"
    >
      {children}
    </button>
  )
}
